// src/utils/timezoneGuardrail.js
// ============================================================
// TCPA / FDCPA COMPLIANCE — Timezone Guardrail
// Prevents calls outside 8:00 AM – 9:00 PM in the tenant's
// local time zone, based on their phone's area code.
//
// FDCPA § 805(a)(1): No communication at "an unusual time or
// place or a time or place known or which should be known to
// be inconvenient to the consumer." Courts have held 8am-9pm
// local time is the safe window.
// ============================================================

// Area code → IANA timezone string
// Covers all 50 states + DC. Sorted by area code.
const AREA_CODE_TIMEZONE = {
  // Alaska
  907: 'America/Anchorage',
  // Hawaii
  808: 'Pacific/Honolulu',
  // Pacific Time (AZ is Mountain but no DST)
  480: 'America/Phoenix', 520: 'America/Phoenix', 602: 'America/Phoenix',
  623: 'America/Phoenix', 928: 'America/Phoenix',
  // Pacific
  206: 'America/Los_Angeles', 209: 'America/Los_Angeles', 213: 'America/Los_Angeles',
  253: 'America/Los_Angeles', 310: 'America/Los_Angeles', 323: 'America/Los_Angeles',
  360: 'America/Los_Angeles', 408: 'America/Los_Angeles', 415: 'America/Los_Angeles',
  424: 'America/Los_Angeles', 442: 'America/Los_Angeles', 503: 'America/Los_Angeles',
  509: 'America/Los_Angeles', 510: 'America/Los_Angeles', 530: 'America/Los_Angeles',
  541: 'America/Los_Angeles', 559: 'America/Los_Angeles', 562: 'America/Los_Angeles',
  619: 'America/Los_Angeles', 626: 'America/Los_Angeles', 628: 'America/Los_Angeles',
  650: 'America/Los_Angeles', 657: 'America/Los_Angeles', 661: 'America/Los_Angeles',
  669: 'America/Los_Angeles', 702: 'America/Los_Angeles', 707: 'America/Los_Angeles',
  714: 'America/Los_Angeles', 747: 'America/Los_Angeles', 760: 'America/Los_Angeles',
  775: 'America/Los_Angeles', 805: 'America/Los_Angeles', 818: 'America/Los_Angeles',
  831: 'America/Los_Angeles', 858: 'America/Los_Angeles', 909: 'America/Los_Angeles',
  916: 'America/Los_Angeles', 925: 'America/Los_Angeles', 949: 'America/Los_Angeles',
  951: 'America/Los_Angeles',
  // Mountain
  303: 'America/Denver', 307: 'America/Denver', 385: 'America/Denver',
  406: 'America/Denver', 435: 'America/Denver', 505: 'America/Denver',
  575: 'America/Denver', 720: 'America/Denver', 801: 'America/Denver',
  // Central
  210: 'America/Chicago', 214: 'America/Chicago', 217: 'America/Chicago',
  224: 'America/Chicago', 225: 'America/Chicago', 228: 'America/Chicago',
  254: 'America/Chicago', 262: 'America/Chicago', 270: 'America/Chicago',
  281: 'America/Chicago', 309: 'America/Chicago', 312: 'America/Chicago',
  316: 'America/Chicago', 318: 'America/Chicago', 319: 'America/Chicago',
  320: 'America/Chicago', 325: 'America/Chicago', 331: 'America/Chicago',
  337: 'America/Chicago', 346: 'America/Chicago', 361: 'America/Chicago',
  402: 'America/Chicago', 405: 'America/Chicago', 409: 'America/Chicago',
  414: 'America/Chicago', 417: 'America/Chicago', 430: 'America/Chicago',
  432: 'America/Chicago', 469: 'America/Chicago', 479: 'America/Chicago',
  501: 'America/Chicago', 512: 'America/Chicago', 515: 'America/Chicago',
  563: 'America/Chicago', 573: 'America/Chicago', 580: 'America/Chicago',
  601: 'America/Chicago', 608: 'America/Chicago', 612: 'America/Chicago',
  615: 'America/Chicago', 620: 'America/Chicago', 630: 'America/Chicago',
  636: 'America/Chicago', 641: 'America/Chicago', 651: 'America/Chicago',
  662: 'America/Chicago', 682: 'America/Chicago', 708: 'America/Chicago',
  712: 'America/Chicago', 713: 'America/Chicago', 715: 'America/Chicago',
  731: 'America/Chicago', 737: 'America/Chicago', 763: 'America/Chicago',
  785: 'America/Chicago', 806: 'America/Chicago', 817: 'America/Chicago',
  830: 'America/Chicago', 832: 'America/Chicago', 870: 'America/Chicago',
  901: 'America/Chicago', 903: 'America/Chicago', 913: 'America/Chicago',
  915: 'America/Chicago', 918: 'America/Chicago', 920: 'America/Chicago',
  940: 'America/Chicago', 952: 'America/Chicago', 956: 'America/Chicago',
  972: 'America/Chicago', 979: 'America/Chicago', 985: 'America/Chicago',
  // Eastern
  201: 'America/New_York', 202: 'America/New_York', 203: 'America/New_York',
  207: 'America/New_York', 212: 'America/New_York', 215: 'America/New_York',
  216: 'America/New_York', 218: 'America/New_York', 219: 'America/New_York',
  229: 'America/New_York', 231: 'America/New_York', 234: 'America/New_York',
  239: 'America/New_York', 240: 'America/New_York', 248: 'America/New_York',
  251: 'America/New_York', 252: 'America/New_York', 256: 'America/New_York',
  260: 'America/New_York', 267: 'America/New_York', 269: 'America/New_York',
  272: 'America/New_York', 276: 'America/New_York', 301: 'America/New_York',
  302: 'America/New_York', 304: 'America/New_York', 305: 'America/New_York',
  313: 'America/New_York', 315: 'America/New_York', 321: 'America/New_York',
  330: 'America/New_York', 332: 'America/New_York', 334: 'America/New_York',
  336: 'America/New_York', 339: 'America/New_York', 340: 'America/New_York',
  347: 'America/New_York', 351: 'America/New_York', 352: 'America/New_York',
  380: 'America/New_York', 386: 'America/New_York', 401: 'America/New_York',
  404: 'America/New_York', 407: 'America/New_York', 410: 'America/New_York',
  412: 'America/New_York', 413: 'America/New_York', 419: 'America/New_York',
  423: 'America/New_York', 425: 'America/New_York', 434: 'America/New_York',
  440: 'America/New_York', 443: 'America/New_York', 445: 'America/New_York',
  456: 'America/New_York', 463: 'America/New_York', 470: 'America/New_York',
  475: 'America/New_York', 478: 'America/New_York', 484: 'America/New_York',
  516: 'America/New_York', 517: 'America/New_York', 518: 'America/New_York',
  540: 'America/New_York', 551: 'America/New_York', 561: 'America/New_York',
  567: 'America/New_York', 570: 'America/New_York', 571: 'America/New_York',
  574: 'America/New_York', 585: 'America/New_York',
  586: 'America/New_York', 603: 'America/New_York', 607: 'America/New_York',
  609: 'America/New_York', 610: 'America/New_York', 614: 'America/New_York',
  616: 'America/New_York', 617: 'America/New_York', 631: 'America/New_York',
  646: 'America/New_York', 678: 'America/New_York', 680: 'America/New_York',
  689: 'America/New_York', 703: 'America/New_York', 704: 'America/New_York',
  706: 'America/New_York', 716: 'America/New_York', 717: 'America/New_York',
  718: 'America/New_York', 724: 'America/New_York', 727: 'America/New_York',
  732: 'America/New_York', 734: 'America/New_York', 740: 'America/New_York',
  743: 'America/New_York', 754: 'America/New_York', 757: 'America/New_York',
  762: 'America/New_York', 764: 'America/New_York', 772: 'America/New_York',
  774: 'America/New_York', 781: 'America/New_York', 786: 'America/New_York',
  803: 'America/New_York', 804: 'America/New_York', 810: 'America/New_York',
  813: 'America/New_York', 814: 'America/New_York', 828: 'America/New_York',
  843: 'America/New_York', 845: 'America/New_York', 848: 'America/New_York',
  850: 'America/New_York', 856: 'America/New_York', 857: 'America/New_York',
  859: 'America/New_York', 860: 'America/New_York', 862: 'America/New_York',
  863: 'America/New_York', 864: 'America/New_York', 865: 'America/New_York',
  878: 'America/New_York', 904: 'America/New_York', 908: 'America/New_York',
  910: 'America/New_York', 912: 'America/New_York', 914: 'America/New_York',
  917: 'America/New_York', 919: 'America/New_York', 929: 'America/New_York',
  931: 'America/New_York', 937: 'America/New_York', 941: 'America/New_York',
  947: 'America/New_York', 954: 'America/New_York', 959: 'America/New_York',
  973: 'America/New_York', 975: 'America/New_York', 980: 'America/New_York',
  984: 'America/New_York',
};

// TCPA-safe calling window
const CALL_HOUR_START = 8;   // 8:00 AM
const CALL_HOUR_END   = 21;  // 9:00 PM (21:00)

/**
 * Extract the 3-digit area code from a US phone number string.
 * Handles formats: (555) 123-4567, +15551234567, 5551234567, 555-123-4567
 */
function extractAreaCode(phone) {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  // Strip leading country code (1)
  const normalized = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (normalized.length < 10) return null;
  return parseInt(normalized.slice(0, 3), 10);
}

/**
 * Look up the IANA timezone for a US phone number.
 * Falls back to 'America/New_York' (Eastern) if area code is unknown —
 * Eastern is the most restrictive (earliest end time in absolute UTC terms),
 * so it's the safest default.
 */
export function getTimezoneForPhone(phone) {
  const areaCode = extractAreaCode(phone);
  return AREA_CODE_TIMEZONE[areaCode] || 'America/New_York';
}

/**
 * Get the tenant's current local time as a Date object.
 */
export function getTenantLocalTime(phone) {
  const tz = getTimezoneForPhone(phone);
  // Use Intl to get a date string in the tenant's timezone, then parse it
  const localStr = new Date().toLocaleString('en-US', { timeZone: tz });
  return new Date(localStr);
}

/**
 * Returns true if it is currently safe to call this number (TCPA compliant).
 * Safe window: 8:00 AM – 9:00 PM local time.
 *
 * @param {string} phone - Tenant phone number
 * @returns {{ safe: boolean, reason: string, localTime: string, timezone: string }}
 */
export function isSafeToCall(phone) {
  const tz = getTimezoneForPhone(phone);
  const localTime = getTenantLocalTime(phone);
  const hour = localTime.getHours();
  const minute = localTime.getMinutes();
  const timeStr = localTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

  const safe = hour >= CALL_HOUR_START && (hour < CALL_HOUR_END || (hour === CALL_HOUR_END && minute === 0));

  let reason = '';
  if (!safe) {
    if (hour < CALL_HOUR_START) {
      reason = `Too early — it is ${timeStr} for this tenant. Calling resumes at 8:00 AM their time.`;
    } else {
      reason = `Too late — it is ${timeStr} for this tenant. Calling stopped at 9:00 PM their time.`;
    }
  }

  return { safe, reason, localTime: timeStr, timezone: tz };
}

/**
 * Format a timezone string for display.
 * e.g. 'America/New_York' → 'Eastern Time'
 */
export function formatTimezone(tz) {
  const map = {
    'America/New_York':   'Eastern Time',
    'America/Chicago':    'Central Time',
    'America/Denver':     'Mountain Time',
    'America/Phoenix':    'Arizona Time',
    'America/Los_Angeles':'Pacific Time',
    'America/Anchorage':  'Alaska Time',
    'Pacific/Honolulu':   'Hawaii Time',
  };
  return map[tz] || tz;
}
